/**
 * Marketplace API client (`/v1/billing-saas/*`) and TanStack Query hooks.
 *
 * Every query key starts with `marketplace`, so the shell's live connection
 * refetches it on `marketplace.entitlement` and `marketplace.request` events
 * (another admin, another tab, a Square webhook). The shell bootstrap (and
 * with it every locked panel) refetches on `shell.entitlement`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionResultSchema,
  BillingNoticeSchema,
  BillingOverviewSchema,
  FeatureAccessResponseSchema,
  MarketplaceSettingsSchema,
  type MarketplaceSettingsPatch,
  ModuleDetailResponseSchema,
  ModuleRequestListSchema,
  ModuleRequestSchema,
  SaveCardResultSchema,
  StoreResponseSchema,
  TurnOffPreviewSchema,
  type ActionResult,
  type DecideModuleRequestBody,
  type MarketplaceModuleKey,
  type SaveCardBody,
  type StartTrialBody,
  type TurnOffBody,
  type TurnOnBody,
} from '@velora/contracts/marketplace';
import { ApiError } from '@velora/sdk';
import { z } from 'zod';
import { api } from '../../app/api.js';

const BASE = 'billing-saas';

export const marketplaceKeys = {
  all: ['marketplace'] as const,
  store: ['marketplace', 'store'] as const,
  module: (key: string) => ['marketplace', 'module', key] as const,
  turnOff: (key: string, locationId: string) =>
    ['marketplace', 'turn-off', key, locationId] as const,
  requests: ['marketplace', 'requests'] as const,
  overview: ['marketplace', 'overview'] as const,
  notice: ['marketplace', 'notice'] as const,
  settings: ['marketplace', 'settings'] as const,
};

export const marketplaceApi = {
  settings: () => api.request(`${BASE}/settings`, { schema: MarketplaceSettingsSchema }),
  saveSettings: (body: MarketplaceSettingsPatch) =>
    api.request(`${BASE}/settings`, { method: 'PUT', body, schema: MarketplaceSettingsSchema }),
  store: (signal?: AbortSignal) =>
    api.request(`${BASE}/store`, { schema: StoreResponseSchema, ...(signal ? { signal } : {}) }),
  module: (key: string, signal?: AbortSignal) =>
    api.request(`${BASE}/modules/${encodeURIComponent(key)}`, {
      schema: ModuleDetailResponseSchema,
      ...(signal ? { signal } : {}),
    }),
  turnOffPreview: (key: string, locationId: string) =>
    api.request(
      `${BASE}/modules/${encodeURIComponent(key)}/turn-off-preview?locationId=${encodeURIComponent(locationId)}`,
      { schema: TurnOffPreviewSchema },
    ),
  trial: (key: string, body: StartTrialBody) =>
    api.request(`${BASE}/modules/${key}/trial`, {
      method: 'POST',
      body,
      schema: ActionResultSchema,
    }),
  turnOn: (key: string, body: TurnOnBody) =>
    api.request(`${BASE}/modules/${key}/turn-on`, {
      method: 'POST',
      body,
      schema: ActionResultSchema,
    }),
  turnOff: (key: string, body: TurnOffBody) =>
    api.request(`${BASE}/modules/${key}/turn-off`, {
      method: 'POST',
      body,
      schema: ActionResultSchema,
    }),
  undoTurnOff: (key: string, locationIds: string[]) =>
    api.request(`${BASE}/modules/${key}/undo-turn-off`, {
      method: 'POST',
      body: { locationIds },
      schema: ActionResultSchema,
    }),
  requests: () => api.request(`${BASE}/requests`, { schema: ModuleRequestListSchema }),
  createRequest: (body: { moduleKey: MarketplaceModuleKey; locationId: string; reason?: string }) =>
    api.request(`${BASE}/requests`, { method: 'POST', body, schema: ModuleRequestSchema }),
  decideRequest: (id: string, body: DecideModuleRequestBody) =>
    api.request(`${BASE}/requests/${id}/decide`, {
      method: 'POST',
      body,
      schema: ModuleRequestSchema,
    }),
  withdrawRequest: (id: string) =>
    api.request(`${BASE}/requests/${id}/withdraw`, { method: 'POST', schema: ModuleRequestSchema }),
  overview: () => api.request(`${BASE}/overview`, { schema: BillingOverviewSchema }),
  saveCard: (body: SaveCardBody) =>
    api.request(`${BASE}/card`, { method: 'POST', body, schema: SaveCardResultSchema }),
  notice: () => api.request(`${BASE}/notice`, { schema: BillingNoticeSchema }),
  access: (feature: string, locationId: string, mode: 'read' | 'write' = 'read') =>
    api.request(
      `${BASE}/access?feature=${encodeURIComponent(feature)}&locationId=${encodeURIComponent(locationId)}&mode=${mode}`,
      { schema: FeatureAccessResponseSchema },
    ),
  simulateInvoice: (reference: string, outcome: 'paid' | 'failed') =>
    api.request(`${BASE}/simulator/invoices/${encodeURIComponent(reference)}/settle`, {
      method: 'POST',
      body: { outcome },
      schema: z.object({ outcome: z.string() }),
    }),
  simulateRun: (job: 'clock' | 'monthly' | 'reconcile') =>
    api.request(`${BASE}/simulator/run`, {
      method: 'POST',
      body: { job },
      schema: z.object({ ok: z.boolean(), detail: z.string() }),
    }),
};

/**
 * True when the server has no Square billing configured: every
 * /v1/billing-saas route answers 503 (`service_unavailable`; the action
 * results call the same state `billing_unavailable`). Pages
 * then say add-ons are not set up yet instead of "did not load", and
 * nothing retries: the answer will not change until an operator sets it up.
 */
export function isBillingUnavailable(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 503) return false;
  const code = (err.body as { error?: { code?: string } } | undefined)?.error?.code;
  return code === undefined || code === 'billing_unavailable' || code === 'service_unavailable';
}

/** Retry transient failures, never a missing billing setup. */
export function marketplaceRetry(failures: number, err: unknown): boolean {
  return !isBillingUnavailable(err) && failures < 3;
}

export function useStore() {
  return useQuery({
    queryKey: marketplaceKeys.store,
    queryFn: ({ signal }) => marketplaceApi.store(signal),
    retry: marketplaceRetry,
  });
}

export function useModuleDetail(key: string) {
  return useQuery({
    queryKey: marketplaceKeys.module(key),
    queryFn: ({ signal }) => marketplaceApi.module(key, signal),
    retry: marketplaceRetry,
  });
}

export function useRequests(enabled = true) {
  return useQuery({
    queryKey: marketplaceKeys.requests,
    queryFn: () => marketplaceApi.requests(),
    enabled,
    retry: marketplaceRetry,
  });
}

export function useOverview(enabled = true) {
  return useQuery({
    queryKey: marketplaceKeys.overview,
    queryFn: () => marketplaceApi.overview(),
    enabled,
    retry: marketplaceRetry,
  });
}

export function useBillingNotice() {
  return useQuery({
    queryKey: marketplaceKeys.notice,
    queryFn: () => marketplaceApi.notice(),
    retry: marketplaceRetry,
  });
}

/** Invalidates everything Marketplace and the shell bootstrap (locked panels). */
export function useMarketplaceRefresh() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: marketplaceKeys.all }),
      qc.invalidateQueries({ queryKey: ['shell'] }),
    ]);
}

export type ActionKind = 'trial' | 'turn-on' | 'turn-off' | 'undo';

export function useModuleAction() {
  const refresh = useMarketplaceRefresh();
  return useMutation({
    mutationFn: async (
      a:
        | { kind: 'trial'; key: string; body: StartTrialBody }
        | { kind: 'turn-on'; key: string; body: TurnOnBody }
        | { kind: 'turn-off'; key: string; body: TurnOffBody }
        | { kind: 'undo'; key: string; locationIds: string[] },
    ): Promise<ActionResult> => {
      switch (a.kind) {
        case 'trial':
          return marketplaceApi.trial(a.key, a.body);
        case 'turn-on':
          return marketplaceApi.turnOn(a.key, a.body);
        case 'turn-off':
          return marketplaceApi.turnOff(a.key, a.body);
        case 'undo':
          return marketplaceApi.undoTurnOff(a.key, a.locationIds);
      }
    },
    onSettled: () => {
      void refresh();
    },
  });
}

/** A user-facing sentence for an API failure. */
export function errorMessage(err: unknown): string {
  if (isBillingUnavailable(err)) return BILLING_UNAVAILABLE_COPY;
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } } | undefined;
    if (err.status === 403) return body?.error?.message ?? 'You do not have permission to do that.';
    return body?.error?.message ?? 'Something went wrong. Try again.';
  }
  return 'Could not reach Velora. Check the connection and try again.';
}

export const BILLING_UNAVAILABLE_COPY =
  'Add-on billing is not set up on this server yet, so modules cannot be started or changed here. Modules already on keep working. Velora support finishes this setup.';
